import React, { useState, useEffect, useRef } from "react";
import "./cookie-banner.scss";
import {
  CONSENT_KEY,
  CookieConsent,
  loadAnalytics,
  trackPageView,
} from "../../utils/analytics";

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always enabled
    analytics: true,
  });

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) setIsVisible(true);
  }, []);

  // Move focus into the banner when it appears and allow Escape to dismiss it.
  useEffect(() => {
    if (!isVisible) return;

    const dialog = dialogRef.current;
    dialog?.querySelector<HTMLElement>("button")?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") declineCookies();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isVisible]);

  // Persist the choice and start analytics immediately if it was granted.
  const persistConsent = (consent: CookieConsent) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    if (consent.analytics) {
      loadAnalytics();
      trackPageView(window.location.pathname + window.location.search);
    }
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    persistConsent(preferences);
  };

  const acceptAllCookies = () => {
    setPreferences({ necessary: true, analytics: true });
    persistConsent({ necessary: true, analytics: true });
  };

  const declineCookies = () => {
    setPreferences({ necessary: true, analytics: false });
    persistConsent({ necessary: true, analytics: false });
  };

  const handlePreferenceChange = (key: string) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    isVisible && (
      <div
        ref={dialogRef}
        className="cookie-banner"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent banner"
      >
        {!showPreferences ? (
          <>
            <p>
              We use cookies to enhance your browsing experience and analyse our traffic. By clicking "Accept all", you consent to our use of cookies. Learn more in our{" "}
              <a href="/privacy-and-gdpr">Privacy Policy</a>.
            </p>
            <div className="cookie-buttons">
              <button className="btn-outline-primary accept-all" onClick={acceptAllCookies} tabIndex={0}>
                Accept All
              </button>
              <button className="btn-outline-primary" onClick={() => setShowPreferences(true)} tabIndex={0}>
                Preferences
              </button>
              <button className="btn-outline-primary" onClick={declineCookies} tabIndex={0}>
                Decline
              </button>
            </div>
          </>
        ) : (
          <div className="cookie-preferences-container">
            <div className="content">
              <p>Select your cookie preferences:</p>
              <div className="cookie-preferences">
                <label>
                  <input
                    type="checkbox"
                    checked
                    disabled
                  />
                  Necessary Cookies (Always Enabled)
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => handlePreferenceChange("analytics")}
                  />
                  Analytics Cookies
                </label>
              </div>
            </div>
            <div className="cookie-buttons">
              <button className="btn-outline-primary" onClick={handleSavePreferences} tabIndex={0}>
                Save Preferences
              </button>
              <button className="btn-outline-primary" onClick={() => setShowPreferences(false)} tabIndex={0}>
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    )
  );
};

export default CookieBanner;
